import { Injectable } from '@angular/core';
import { APPWRITE_CONFIG, generateId, toAppwriteStorageBucketId } from '@prism/prism-shared';
import { Account, Permission, Query, Role, Storage } from 'appwrite';
import { Observable, ReplaySubject, catchError, from, map, of, switchMap, throwError } from 'rxjs';

import { AppwriteClientService } from '../tokens/appwrite-client.token';

// Appwrite URLs are deterministic from bucket+file+project+endpoint. They do not
// embed a token; access is controlled by Appwrite bucket and file permissions.
export const StorageBucket = APPWRITE_CONFIG.buckets;
export type StorageBucket = typeof StorageBucket[keyof typeof StorageBucket];

export interface CompatUploadSnapshot {
    state: 'running' | 'success';
    ref: CompatStorageRef;
    bytesTransferred: number;
    totalBytes: number;
}

export class CompatStorageRef {
    constructor(
        private readonly storage: Storage,
        private readonly bucketId: string,
        private readonly fileId: string,
    ) { }

    public put(file: Blob): CompatUploadTask {
        return new CompatUploadTask(this.storage, this.bucketId, this.fileId, file, this);
    }

    public getDownloadURL(): Observable<string> {
        return of(this.storage.getFileView({ bucketId: this.bucketId, fileId: this.fileId }).toString());
    }

    public delete(): Observable<void> {
        return from(this.storage.deleteFile({ bucketId: this.bucketId, fileId: this.fileId })).pipe(
            map(() => void 0),
        );
    }
}

export class CompatUploadTask implements PromiseLike<CompatUploadSnapshot> {
    private readonly uploadPromise: Promise<CompatUploadSnapshot>;
    private readonly progressSubject = new ReplaySubject<number>(1);

    constructor(
        private readonly storage: Storage,
        private readonly bucketId: string,
        private readonly fileId: string,
        private readonly file: Blob,
        public readonly ref: CompatStorageRef,
    ) {
        this.uploadPromise = this.upload();
    }

    public snapshotChanges(): Observable<CompatUploadSnapshot> {
        return from(this.uploadPromise);
    }

    public percentageChanges(): Observable<number> {
        return this.progressSubject.asObservable();
    }

    public then<TResult1 = CompatUploadSnapshot, TResult2 = never>(
        onfulfilled?: ((value: CompatUploadSnapshot) => TResult1 | PromiseLike<TResult1>) | null,
        onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null,
    ): PromiseLike<TResult1 | TResult2> {
        return this.uploadPromise.then(onfulfilled, onrejected);
    }

    private async upload(): Promise<CompatUploadSnapshot> {
        const appwriteFile = this.file instanceof File
            ? this.file
            : new File([this.file], `${this.fileId}`, { type: this.file.type || 'application/octet-stream' });

        this.progressSubject.next(0);
        try {
            await this.createFile(appwriteFile);
            this.progressSubject.next(100);
            this.progressSubject.complete();
            const totalBytes = appwriteFile.size;
            return { state: 'success', ref: this.ref, bytesTransferred: totalBytes, totalBytes };
        } catch (error) {
            this.progressSubject.complete();
            throw error;
        }
    }

    private async createFile(appwriteFile: File): Promise<void> {
        try {
            await this.storage.createFile({
                bucketId: this.bucketId,
                fileId: this.fileId,
                file: appwriteFile,
                onProgress: progress => this.progressSubject.next(progress.progress),
            });
            return;
        } catch (err) {
            if (!this.isConflict(err)) {
                throw err;
            }
        }

        await this.storage.deleteFile({ bucketId: this.bucketId, fileId: this.fileId });
        await this.storage.createFile({
            bucketId: this.bucketId,
            fileId: this.fileId,
            file: appwriteFile,
            onProgress: progress => this.progressSubject.next(progress.progress),
        });
    }

    private isConflict(err: unknown): boolean {
        return typeof err === 'object'
            && err !== null
            && 'code' in err
            && (err as { code?: number }).code === 409;
    }
}

@Injectable({ providedIn: 'root' })
export class AppwriteStorageService {
    private readonly storage: Storage;
    private readonly account: Account;
    private readonly appwrite: AppwriteClientService;

    constructor(appwrite: AppwriteClientService) {
        this.appwrite = appwrite;
        this.storage = new Storage(appwrite.client);
        this.account = new Account(appwrite.client);
    }

    public upload(path: string, file: Blob): CompatUploadTask {
        const { bucketId, fileId } = this.toBucketAndFileId(path);
        return new CompatStorageRef(this.storage, bucketId, fileId).put(file);
    }

    public ref(path: string): CompatStorageRef {
        const { bucketId, fileId } = this.toBucketAndFileId(path);
        return new CompatStorageRef(this.storage, bucketId, fileId);
    }

    public upload$(bucket: StorageBucket | string, fileId: string, blob: Blob, fileName: string): Observable<{ id: string }> {
        const file = this.toFile(blob, fileName);
        return from(this.storage.createFile({ bucketId: bucket, fileId, file })).pipe(
            map(resp => ({ id: resp.$id })),
        );
    }

    /** Creates a file that only its authenticated creator (and server API keys) may mutate. */
    public uploadUserOwned$(bucket: StorageBucket | string, fileId: string, blob: Blob, fileName: string): Observable<{ id: string }> {
        const file = this.toFile(blob, fileName);
        return from(this.account.get()).pipe(
            switchMap(account => from(this.storage.createFile({
                bucketId: bucket,
                fileId,
                file,
                permissions: [
                    Permission.read(Role.users()),
                    Permission.update(Role.user(account.$id)),
                    Permission.delete(Role.user(account.$id)),
                ],
            }))),
            map(response => ({ id: response.$id })),
            // A retry may find the exact preallocated file already uploaded. The
            // server association step verifies both ownership and contents.
            catchError(error => this.isConflict(error) ? of({ id: fileId }) : throwError(() => error)),
        );
    }

    public uploadOrReplace$(bucket: StorageBucket | string, fileId: string, blob: Blob, fileName: string): Observable<{ id: string }> {
        const file = this.toFile(blob, fileName);
        return from((async () => {
            try {
                const response = await this.storage.createFile({ bucketId: bucket, fileId, file });
                return { id: response.$id };
            } catch (err) {
                if (!this.isConflict(err)) throw err;
            }
            await this.storage.deleteFile({ bucketId: bucket, fileId });
            const response = await this.storage.createFile({ bucketId: bucket, fileId, file });
            return { id: response.$id };
        })());
    }

    public getFileViewUrl(bucket: StorageBucket | string, fileId: string): string {
        return this.storage.getFileView({ bucketId: bucket, fileId }).toString();
    }

    public getFileDownloadUrl(bucket: StorageBucket | string, fileId: string): string {
        return this.storage.getFileDownload({ bucketId: bucket, fileId }).toString();
    }

    /**
     * Loads private file bytes through the Appwrite client so local development
     * sessions can use Appwrite's fallback-cookie authentication header.
     */
    public getFileBlob$(
        bucket: StorageBucket | string,
        fileId: string,
        mimeType = 'application/octet-stream',
    ): Observable<Blob> {
        const url = new URL(this.storage.getFileView({ bucketId: bucket, fileId }));
        const request = this.appwrite.client.call('GET', url, undefined, undefined, 'arrayBuffer') as Promise<ArrayBuffer>;
        return from(request).pipe(
            map((bytes) => new Blob([bytes], { type: mimeType })),
        );
    }

    public getFileMetadata$(bucket: StorageBucket | string, fileId: string): Observable<{
        id: string;
        name: string;
        mimeType: string;
        sizeOriginal: number;
    }> {
        return from(this.storage.getFile({ bucketId: bucket, fileId })).pipe(
            map(file => ({
                id: file.$id,
                name: file.name,
                mimeType: file.mimeType,
                sizeOriginal: file.sizeOriginal,
            })),
        );
    }

    /** Looks up a file without producing a 404 request when it is absent. */
    public findFileMetadata$(bucket: StorageBucket | string, fileId: string): Observable<{
        id: string;
        name: string;
        mimeType: string;
        sizeOriginal: number;
    } | null> {
        return from(this.storage.listFiles({
            bucketId: bucket,
            queries: [Query.equal('$id', fileId), Query.limit(1)],
        })).pipe(
            map(response => {
                const file = response.files[0];
                return file ? {
                    id: file.$id,
                    name: file.name,
                    mimeType: file.mimeType,
                    sizeOriginal: file.sizeOriginal,
                } : null;
            }),
        );
    }

    public delete$(bucket: StorageBucket | string, fileId: string): Observable<void> {
        return from(this.storage.deleteFile({ bucketId: bucket, fileId })).pipe(
            map(() => void 0),
        );
    }

    public createId(): string {
        let chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let autoId = '';
        for (let i = 0; i < 20; i++) {
            autoId += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return autoId;
    }

    private toBucketAndFileId(path: string): { bucketId: string; fileId: string } {
        const [folder, ...rest] = path.split('/');
        const bucketId = this.toBucketId(folder);
        const logicalPath = rest.length ? rest.join('/') : folder;
        return {
            bucketId,
            fileId: this.toStableFileId(logicalPath || path),
        };
    }

    private toBucketId(folder: string): string {
        return toAppwriteStorageBucketId(folder);
    }

    private toStableFileId(value: string): string {
        let hash = 5381;
        for (let i = 0; i < value.length; i++) {
            hash = ((hash << 5) + hash) + value.charCodeAt(i);
            hash = hash >>> 0;
        }
        return `f_${hash.toString(36)}`;
    }

    private toFile(blob: Blob, fileName: string): File {
        return blob instanceof File
            ? blob
            : new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
    }

    private isConflict(err: unknown): boolean {
        return typeof err === 'object'
            && err !== null
            && 'code' in err
            && (err as { code?: number }).code === 409;
    }
}
